/**
 * firmaMediaService.js — firma fotoğraf galerisi, harita konumu ve katalog (kategoriler +
 * açıklama) YAZMA uçları. Backlog #60, backend #61.
 *
 * CANLI (mock yok) — firmaProfileService.js'in aksine burada USE_MOCK_DATA yok: bu uçlar
 * 2026-09-18'de yeni açıldı, mock karşılıkları hiç olmadı ve galeri/konum düzenleme zaten
 * yalnızca gerçek backend'e karşı anlamlı (aynı gerekçeyle FirmaProfilIncelemePage.jsx de
 * doğrudan canlıya kuruldu).
 *
 * OKUMA burada DEĞİL: `GET /firma/{id}/profile-page` zaten `fotograflar[]` ile birlikte
 * `profile.latitude/longitude`, `profile.kategoriDetay[]` ve `profile.firmaLogoUrl` taşıyor,
 * ve her iki sayfa da o çağrıyı zaten yapıyor — galeriyi/konumu/katalogu göstermek için
 * EK BİR İSTEK GEREKMİYOR. Buradaki getFirmaPhotos yalnızca tazeleme içindir.
 *
 * Sözleşme util/knowledgebase/firma-foto-konum-katalog.md'de; docs/guides/FE-FirmaController-guide.md
 * #61'den ESKİ ve bu konularda yanlış (PATCH semantiği, kategori doğrulama, fotoğraf uçlarının
 * tamamı) — rehbere değil, knowledgebase'e bakın.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, JAVA_BACKEND_ORIGIN, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/firma`;

// ─── Backend sınırları (FirmaPhotoConstant / ImageUploadValidator) ────────────
// İstemci tarafında da uygulanır ki kullanıcı 8 MB'lık dosyayı yükledikten sonra
// 400 görmesin — backend yine de kendi kontrolünü yapar, burası sadece erken uyarı.
export const FOTO_MAX_BYTES = 8 * 1024 * 1024;
export const FOTO_MAX_ADET = 20;
export const FOTO_UZANTILAR = ['png', 'jpg', 'jpeg', 'webp']; // SVG kabul EDİLMİYOR
export const KATEGORI_MAX_ADET = 30;
export const KATEGORI_MAX_UZUNLUK = 200;

/**
 * Backend'in verdiği dosya yolunu gösterilebilir bir src'ye çevirir.
 * `firmaLogoUrl` LEGACY'de mutlak (eski PHP sunucusu), UPLOADED'da ve fotoğraflarda
 * backend köküne göre göreli (/public/files/... — /api/v1'in dışında).
 */
export function firmaFileSrc(url) {
  if (!url) return null;
  // blob:/data: → demo (mock) modda yüklenen yerel önizleme; backend yolu değil.
  if (/^(https?:\/\/|blob:|data:)/i.test(url)) return url;
  return `${JAVA_BACKEND_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Hata gövdeleri üç şekilde geliyor (düz metin, {message,result,data}, boş). Ekrana
 * basılabilir tek bir string üret. fuarService.js'teki aynı isimli yardımcının dosya-yerel
 * kopyası — oradan import EDİLMİYOR, çünkü bilge/backlog-43-49 o dosyayı baştan yazıyor
 * ve henüz merge edilmedi (bkz. #61'in kendi notu, aynı gerekçe).
 */
export function apiErrorMessage(error, fallback = i18n.t("common:bilinmeyenHata")) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  if (status === 401) return i18n.t("services:firmaMediaService.oturumGecersiz401LutfenYeniden");
  if (status === 403) return i18n.t("services:firmaMediaService.buFirmaIcinIslemYapma");
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object' && data.message) return data.message;
  return error?.message || fallback;
}

/** GenericResultJson başarısızlığı HTTP 200 + result:false olabilir — onu da hata say. */
function unwrap(data, fallback) {
  if (data && typeof data === 'object' && data.result === false) {
    throw new Error(data.message || fallback);
  }
  return data;
}

/** Yükleme öncesi istemci kontrolü. Sorun varsa mesaj, yoksa null döner. */
export function fotoDosyaHatasi(file, mevcutAdet = 0) {
  if (!file) return i18n.t("services:firmaMediaService.dosyaSecilmedi");
  const ext = String(file.name || '').split('.').pop()?.toLowerCase();
  if (!FOTO_UZANTILAR.includes(ext)) return i18n.t("services:firmaMediaService.fotografPngJpgVeyaWebp");
  if (file.size > FOTO_MAX_BYTES) return 'Dosya en fazla 8 MB olabilir.';
  if (mevcutAdet >= FOTO_MAX_ADET) return i18n.t("services:firmaMediaService.birFirmaEnFazlaFotograf", { FOTO_MAX_ADET });
  return null;
}

// ─── Fotoğraflar ──────────────────────────────────────────────────────────────
// ⚠️ Her uç {firmaId} taşır — #61'in notundaki `PUT /photos/{photoId}` gibi kısa yollar
// backend'de YOK (bkz. knowledgebase). Okuma herkese açık, yazma kendi firman ya da
// personel (staff) ise serbest: denetmen başka bir firmanın galerisini de düzenleyebilir.

/** GET /firma/{firmaId}/photos — DÜZ dizi döner (GenericResultJson zarfı yok). */
export async function getFirmaPhotos(firmaId) {
  const res = await axios.get(`${BASE}/${firmaId}/photos`, { headers: getAuthHeader() });
  return Array.isArray(res.data) ? res.data : [];
}

/**
 * POST /firma/{firmaId}/photos (multipart: file + opsiyonel baslik).
 * İlk fotoğraf otomatik kapak olur. Dönen `data.fotograflar` tazelenmiş galeridir —
 * yeniden okumak yerine o kullanılır (#63'teki aynı desen).
 */
export async function uploadFirmaPhoto(firmaId, file, baslik) {
  const form = new FormData();
  form.append('file', file);
  if (baslik && baslik.trim()) form.append('baslik', baslik.trim());
  const res = await axios.post(`${BASE}/${firmaId}/photos`, form, {
    headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
  });
  const data = unwrap(res.data, i18n.t("services:firmaMediaService.fotografYuklenemedi"));
  return data?.data?.fotograflar || [];
}

/**
 * PUT /firma/{firmaId}/photos/{photoId} — PATCH semantiği: yalnızca gönderilen alan değişir.
 * ⚠️ `kapak: false` HİÇBİR ŞEY YAPMAZ (backend yalnızca true'yu okur); kapağı kaldırmanın
 * yolu başka bir fotoğrafı kapak yapmaktır (setFirmaPhotoCover).
 */
export async function updateFirmaPhoto(firmaId, photoId, patch) {
  const res = await axios.put(`${BASE}/${firmaId}/photos/${photoId}`, patch, { headers: getAuthHeader() });
  const data = unwrap(res.data, i18n.t("services:firmaMediaService.fotografGuncellenemedi"));
  return data?.data || [];
}

/**
 * PUT /firma/{firmaId}/photos/{photoId}/cover — gövdesiz; firma başına tek kapak.
 *
 * ⚠️ BACKEND HATASI (#85, 2026-09-19 canlı testte bulundu): bu uç ve DELETE, döndükleri
 * tazelenmiş listede `kapak` bayrağını MUTASYON ÖNCESİ hâliyle veriyor — sıralama doğru
 * (kapak başa geçiyor, yani yazma gerçekleşmiş) ama her satırda `kapak:false` geliyor.
 * Saklanan veri DOĞRU; yalan söyleyen yalnızca cevap. `is_cover`a dokunmayan iki uç
 * (POST ve PUT .../{photoId}) bayrağı doğru dönüyor.
 *
 * Telafi: cevabı kullanmak yerine listeyi yeniden okuyoruz. Kapak terfi kuralını
 * (is_cover DESC, sira ASC, id ASC) istemcide taklit etmiyoruz — backend düzelince bu
 * ek okuma tek satırda geri alınır.
 */
export async function setFirmaPhotoCover(firmaId, photoId) {
  const res = await axios.put(`${BASE}/${firmaId}/photos/${photoId}/cover`, null, { headers: getAuthHeader() });
  unwrap(res.data, i18n.t("services:firmaMediaService.kapakFotografiGuncellenemedi"));
  return getFirmaPhotos(firmaId);
}

/**
 * DELETE /firma/{firmaId}/photos/{photoId} — YUMUŞAK silme (aktif=0). Dosya diskte kalır ve
 * herkese açık URL'i çalışmaya devam eder; kapak silinirse sıradaki fotoğraf kapak olur.
 */
export async function deleteFirmaPhoto(firmaId, photoId) {
  const res = await axios.delete(`${BASE}/${firmaId}/photos/${photoId}`, { headers: getAuthHeader() });
  unwrap(res.data, i18n.t("services:firmaMediaService.fotografKaldirilamadi"));
  // Kapak silinince sıradaki fotoğraf terfi ediyor ama cevap bunu yansıtmıyor — bkz.
  // setFirmaPhotoCover'daki #85 notu.
  return getFirmaPhotos(firmaId);
}

// ─── Profil (katalog + konum) ─────────────────────────────────────────────────

/**
 * PUT /firma/{firmaId}/profile — PATCH semantiği (#61 ile düzeltildi):
 *   alan yok        → saklı değer korunur
 *   alan var, null  → temizlenir
 *   alan var, ""    → yine temizlenir (backend trim edip null yazar)
 *
 * ⚠️ OKUMA anahtarları ile YAZMA anahtarları AYNI DEĞİL:
 *   okuma  adres / telefon / eposta / webSitesi / katalogAciklama
 *   yazma  firmaAddress / firmaPhone / firmaEmail / firmaWebsite / firmaAbout
 * Çeviri burada yapılır ki çağıran sayfalar okudukları şekli göndersin.
 *
 * `kategoriler` SERBEST METİN dizisidir; gönderildiği anda kategori kümesinin TAMAMINI
 * değiştirir ([] hepsini siler), gönderilmezse dokunulmaz.
 * `latitude`/`longitude` İKİSİ BİRDEN gider (ikisi de null = pini temizler).
 */
export async function saveFirmaProfile(firmaId, patch) {
  const body = {};
  const map = {
    adres: 'firmaAddress',
    telefon: 'firmaPhone',
    eposta: 'firmaEmail',
    webSitesi: 'firmaWebsite',
    katalogAciklama: 'firmaAbout',
  };
  Object.entries(map).forEach(([okuma, yazma]) => {
    if (okuma in patch) body[yazma] = patch[okuma];
  });
  if ('kategoriler' in patch) body.kategoriler = patch.kategoriler;
  if ('latitude' in patch || 'longitude' in patch) {
    body.latitude = patch.latitude ?? null;
    body.longitude = patch.longitude ?? null;
  }
  const res = await axios.put(`${BASE}/${firmaId}/profile`, body, { headers: getAuthHeader() });
  return unwrap(res.data, i18n.t("services:firmaMediaService.firmaProfiliGuncellenemedi"));
}

/**
 * Backlog #103/#104 — FUAR-BAZLI katalog (global katalogtan AYRI, firma_id+fuar_id ile
 * anahtarlı). GET {katalog: {aciklama,...}|null, industries: [{industry_type_id,
 * industry_type_name,...}]} — industries ham industry_type satırları, saveFirmaProfile'ın
 * kategoriler[]'ı gibi düz string listesi DEĞİL; çağıran industry_type_name'i çıkarır.
 */
export async function getFuarFirmaKatalog(firmaId, fuarId) {
  const res = await axios.get(`${BASE}/${firmaId}/fuar/${fuarId}/katalog`, { headers: getAuthHeader() });
  return res.data;
}

/**
 * PATCH semantiği saveFirmaProfile ile birebir aynı: yalnızca gönderilen anahtar değişir.
 * patch: { aciklama?, kategoriler? } — kategoriler serbest metin adları, aynı paylaşılan
 * industry_type sözlüğünden (global katalogla aynı) resolve edilir.
 */
export async function saveFuarFirmaKatalog(firmaId, fuarId, patch) {
  const body = {};
  if ('aciklama' in patch) body.aciklama = patch.aciklama;
  if ('kategoriler' in patch) body.kategoriler = patch.kategoriler;
  const res = await axios.put(`${BASE}/${firmaId}/fuar/${fuarId}/katalog`, body, { headers: getAuthHeader() });
  return unwrap(res.data, i18n.t("services:firmaMediaService.fuarKatalogBilgileriGuncellenemedi"));
}

/**
 * Koordinat çifti doğrulaması — backend'in kurallarının aynısı, ama kullanıcı Kaydet'e
 * basmadan önce. Sorun varsa mesaj, yoksa null döner.
 */
export function koordinatHatasi(lat, lng) {
  const bosMu = (v) => v === null || v === undefined || String(v).trim() === '';
  if (bosMu(lat) && bosMu(lng)) return null; // ikisi de boş = pini temizle, geçerli
  if (bosMu(lat) || bosMu(lng)) return i18n.t("services:firmaMediaService.konumIcinEnlemVeBoylam");
  const enlem = Number(lat);
  const boylam = Number(lng);
  if (!Number.isFinite(enlem) || !Number.isFinite(boylam)) return i18n.t("services:firmaMediaService.gecersizKoordinat");
  if (enlem < -90 || enlem > 90) return i18n.t("services:firmaMediaService.enlem90Ile90Arasinda");
  if (boylam < -180 || boylam > 180) return i18n.t("services:firmaMediaService.boylam180Ile180Arasinda");
  return null;
}

/** Kategori listesi doğrulaması. Sorun varsa mesaj, yoksa null döner. */
export function kategoriHatasi(kategoriler) {
  if (kategoriler.length > KATEGORI_MAX_ADET) return i18n.t("services:firmaMediaService.enFazlaKategoriSecilebilir", { KATEGORI_MAX_ADET });
  if (kategoriler.some((k) => k.length > KATEGORI_MAX_UZUNLUK)) {
    return i18n.t("services:firmaMediaService.kategoriAdiEnFazlaKarakter", { KATEGORI_MAX_UZUNLUK });
  }
  return null;
}

/**
 * Bir kategori adını backend'in sakladığı hâle getirir (trim + iç boşlukları tekle).
 * Tekrar kontrolü de bunun üzerinden yapılır — backend adları büyük/küçük harf ve boşluk
 * duyarsız eşleştirdiği için karşılaştırma locale-aware lowercase ile yapılır.
 */
export function kategoriNormalize(ad) {
  return String(ad || '').trim().replace(/\s+/g, ' ');
}
export function kategoriAyniMi(a, b) {
  return kategoriNormalize(a).toLocaleLowerCase('tr-TR') === kategoriNormalize(b).toLocaleLowerCase('tr-TR');
}
